import React from 'react';

import { UnitProperties } from '../index';
import Image from '../layout/Image';

import thumbImage from '../../images/placeholder.gif';

import styles from './locationStyles.module.scss';

interface Props {
  item: UnitFragment;
}

// Helper function to extract unit number from title or slug
const getUnitNumber = (title: string, slug: string): string => {
  // Try to match various patterns in the title:
  // Can be at start: "Apartment 308 - ...", "Villa 14 - ...", "Unit 5 - ..."
  // Can be in middle: "... - Unit 30 - ...", "... Unit 33 SOLD"
  const titlePatterns = [
    // At the beginning with dash
    /^(Apartment|Apt\.?)\s+(\d+[A-Za-z]?)\s*-/i,  // Apartment 308 -, Apt 208 -
    /^(Villa)\s+(\d+[A-Za-z]?)\s*-/i,              // Villa 14 -
    /^(Unit)\s+(\d+[A-Za-z]?)\s*-/i,               // Unit 5 -
    // At the beginning without dash (e.g., "Unit 33 SOLD")
    /^(Apartment|Apt\.?)\s+(\d+[A-Za-z]?)\s/i,    // Apartment 308 (space)
    /^(Villa)\s+(\d+[A-Za-z]?)\s/i,                // Villa 14 (space)
    /^(Unit)\s+(\d+[A-Za-z]?)\s/i,                 // Unit 5 (space)
    // In the middle: "... - Unit 30 - ..."
    /-\s*(Apartment|Apt\.?)\s+(\d+[A-Za-z]?)\s*-/i,
    /-\s*(Villa)\s+(\d+[A-Za-z]?)\s*-/i,
    /-\s*(Unit)\s+(\d+[A-Za-z]?)\s*-/i,
  ];

  for (const pattern of titlePatterns) {
    const match = title.match(pattern);
    if (match) {
      // Normalize "Apt" or "Apt." to "Apartment"
      const type = match[1].toLowerCase().startsWith('apt') ? 'Apartment' : match[1];
      return `${type} ${match[2]}`;
    }
  }

  // If no match in title, try to extract from slug
  // Patterns: "apartment-308-...", "villa-14-...", "unit-5-...", "apt-208-..."
  const slugPatterns = [
    /(?:apartment|apt)-(\d+[a-z]?)/i,  // apartment-308, apt-208
    /villa-(\d+[a-z]?)/i,               // villa-14
    /unit-(\d+[a-z]?)/i,                // unit-5
  ];

  for (const pattern of slugPatterns) {
    const match = slug.match(pattern);
    if (match) {
      // Determine type from the slug pattern
      if (slug.includes('apartment') || slug.includes('apt')) {
        return `Apartment ${match[1]}`;
      } else if (slug.includes('villa')) {
        return `Villa ${match[1]}`;
      } else if (slug.includes('unit')) {
        return `Unit ${match[1]}`;
      }
    }
  }

  // If no pattern matches, return empty string
  return '';
};

// Helper function to extract the first sentence from HTML content
const getFirstSentence = (htmlContent: string | null | undefined): string => {
  if (!htmlContent) return '';

  // Strip HTML tags
  let text = htmlContent.replace(/<[^>]*>/g, '').trim();

  // Find the first full stop and cut the text there
  const firstFullStop = text.indexOf('.');
  if (firstFullStop !== -1) {
    text = text.substring(0, firstFullStop + 1);
  }

  return text;
};

const UnitCard = ({ item }: Props) => {
  const title = item.title!;
  const slug = item.slug!;
  const images = item.photos;
  const unitNumber = getUnitNumber(title, slug);
  const firstSentence = getFirstSentence(item.body);

  return (
    <div className={styles.cardItem}>
      <a className={styles.thumbImage} href={`/${item.uri}`} aria-label={title} title={'Click to view ' + title}>
        {images ? <Image {...images[0]} alt={title} /> : <img loading="lazy" src={thumbImage} alt={title} />}
      </a>

      <div className={styles.cardContent}>
        {unitNumber && (
          <div className={styles.unitNumber}>{unitNumber}</div>
        )}

        <UnitProperties unit={item} />

        {firstSentence && (
          <p className={styles.description}>{firstSentence}</p>
        )}

        <div className={styles.urlContainer}>
          <a className={styles.linkEffects} href={`/${item.uri}`} title={'Click to view ' + title}>
            View package details <span>&#8250;</span>
          </a>
        </div>
      </div>
    </div>
  );
};

export default UnitCard;
